"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { detectLocale, getLocalePath, HINDI_SLUGS, localeLabel, locales } from "@/i18n/locales";
import { legacyCrawlCanonicalPath } from "@/lib/crawl-policy";

/**
 * Language toggle — honest contract (2026-07-08 rebuild):
 *   exact twin exists      → switch IN PLACE to the counterpart page
 *   only the topic hub     → switch to the hub (nested en page, hi hub only)
 *   no twin at all         → the chip does NOT render (never an unrelated page)
 *
 * The old fallback sent every twin-less page to /hi/aaj-ka-rashifal/ — the
 * "I pressed Hindi and some other page loaded" bug. HINDI_SLUGS is now DERIVED
 * from the filesystem scan (locale-twins.generated.ts) + the D1-served festival
 * list, so "twin exists" can no longer drift from reality.
 */
export default function LanguageToggle() {
  const pathname = usePathname();
  const activeLocale = detectLocale(pathname);
  // Strip the locale prefix → [slug, ...subPath]. The slug (first segment) may
  // carry a per-locale alias; deeper segments (e.g. the planet on
  // /lal-kitab-remedies/<planet>/) are locale-invariant when the counterpart
  // side actually serves them.
  const segments = pathname.split("/").filter(Boolean);
  const pathSegments = segments[0] === "hi" ? segments.slice(1) : segments;
  const slug = pathSegments[0] ?? "";
  const subPath = pathSegments.slice(1).join("/");

  return (
    <div className="flex gap-1.5">
      {locales.map((locale) => {
        if (locale === activeLocale) return null;
        if (locale === "hi") {
          // No Hindi twin → offer nothing rather than an unrelated page.
          if (!slug || !HINDI_SLUGS.has(slug)) return null;
          // Every hi twin is a top-level hub today (no nested hi literal
          // routes) — a nested en page switches to its localized hub, the
          // closest real counterpart (never a /hi/X/Y/ that 404s).
          const href = getLocalePath(slug, "hi");
          return (
            <ToggleChip key={locale} href={href} title="हिंदी में पढ़ें">
              {localeLabel[locale]}
            </ToggleChip>
          );
        }
        // → en: every hi page is a localization of a real en surface; keep the
        // nested sub-path (a hi nested page's en counterpart always resolves —
        // this kills the lal-kitab hub 301 that trips the live-4xx audit).
        const base = getLocalePath(slug, "en");
        const href = subPath ? `${base.replace(/\/$/, "")}/${subPath}/` : base;
        return (
          <ToggleChip key={locale} href={href} title="Switch to English">
            {localeLabel[locale]}
          </ToggleChip>
        );
      })}
    </div>
  );
}

function ToggleChip({
  href,
  title,
  children,
}: {
  href: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={legacyCrawlCanonicalPath(href) ?? href}
      prefetch={false}
      className="inline-flex min-h-11 items-center rounded-md border border-border-default px-2.5 py-1.5 text-xs font-semibold text-text-secondary transition hover:bg-surface-brand-subtle"
      title={title}
    >
      {children}
    </Link>
  );
}
